// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyLangValue, TyLatLng } from "@/types";
import MapInput from "@/UI/inputs/MapInput";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyBulkFieldDetails = {
    title: string;
    /** `lang` is a tenant-authored bilingual field; `text` is a plain one; `location` is a point. */
    kind: "lang" | "text" | "location";
    /** What the bar's draft holds, or `undefined` while the field is being left alone. */
    value: unknown;
    /** A sentence saying what this will do to every record in the selection. */
    hint: string;
    onConfirm: (value: unknown) => void;
    /** Back to leaving the field alone — which is not the same as writing it empty. */
    onClear: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * The three bulk fields that are too big for the bar: a description, a note, a point on a map.
 *
 * One popup rather than three, because the difference between them is which input is drawn — the
 * rest, and all of what actually matters here, is the same job: showing whether the field is being
 * written at all, and giving a way back out of writing it.
 *
 * **That way out is the whole point.** In a bulk edit, "empty" and "leave alone" are different
 * instructions with very different consequences — one blanks the description on forty units and the
 * other touches none of them — and an input on its own cannot tell them apart. So the state is said
 * in words at the top, and `Undo` is a button rather than a matter of emptying the box.
 */
export default function BulkFieldPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { title, kind, value, hint, onConfirm, onClear } = details as TyBulkFieldDetails;

    const [draft, setDraft] = useState<unknown>(() => value);

    /** Whether the field is in the write at all. `undefined` is the one value that means "it is not". */
    const isWriting = draft !== undefined;

    const handleSave = () => {
        onConfirm(draft);
        onClose();
    };

    const handleClear = () => {
        onClear();
        onClose();
    };

    return (
        <section className="popup-card bulk-field">
            <h2 className="bulk-field__title">{title}</h2>
            <p className="bulk-field__hint">{isWriting ? hint : t.bulk.keep}</p>

            <div className="bulk-field__body">
                {kind === "lang" && (
                    <MultiLangInput label={title} multiline value={(draft as TyLangValue[] | undefined) ?? []} onChange={(next) => setDraft(next)} />
                )}

                {kind === "text" && (
                    <TextInput label={title} multiline value={(draft as string | undefined) ?? ""} onChange={(next) => setDraft(next)} />
                )}

                {kind === "location" && (
                    <MapInput label={title} value={(draft as TyLatLng | undefined) ?? null} onChange={(next) => setDraft(next)} />
                )}
            </div>

            <div className="bulk-field__actions">
                <button type="button" className="bulk-field__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>

                {/* Only once there is something to undo — on a field nobody has touched it is noise. */}
                {isWriting && (
                    <button type="button" className="bulk-field__revert" onClick={handleClear}>
                        {t.bulk.revertAll}
                    </button>
                )}

                <button type="button" className="bulk-field__save" onClick={handleSave}>
                    {t.common.done}
                </button>
            </div>
        </section>
    );
}
